/**
 * The docs' page on money: how Morcast earns, how a campaign's budget is split by one fixed
 * formula, how a creator's payout follows from it, and which tokens a campaign uses. The figures
 * follow the V1 specification's settlement rules and its first numerical example
 * (docs/specification.md, sections 9 and 13).
 */

import { Link } from "react-router";

import { styles } from "../../components/ui.js";
import { DocPage, DocSection, DocSubheading, DocTable, Formula, P, Points } from "./parts.js";

export function BusinessModel() {
  return (
    <DocPage
      group="Morcast"
      title="Business model"
      lead="Morcast earns 20% of what a brand actually pays, and only when a campaign delivers. A brand sets the price of reach itself, and one fixed formula splits the budget."
    >
      <DocSection id="earning" title="How Morcast earns">
        <Points
          items={[
            {
              lead: "Paid on results only.",
              text: "A brand pays for recognized reach. If nothing is delivered, the brand gets its whole budget back and Morcast earns nothing.",
            },
            {
              lead: "No other charges.",
              text: "Sending a campaign request is free. There is no setup fee and no subscription.",
            },
            {
              lead: "What the fee pays for.",
              text: "It covers the measurement, the checks by models and by people, and the running of the platform.",
            },
            {
              lead: "The fee is fixed in the contract.",
              text: "The escrow takes one fifth of what a campaign spends. Morcast cannot raise it for a campaign.",
            },
          ]}
        />
        <DocTable
          caption="What each party gets from a campaign"
          head={["Who", "What they get"]}
          rows={[
            ["Brand", "Verified organic reach at the price it set, and the unspent budget back"],
            ["Creators", "80% of what the brand pays, in proportion to their reach"],
            ["Morcast", "20% of what the brand pays"],
          ]}
        />
      </DocSection>

      <DocSection id="split" title="How a budget is split">
        <P>
          The price of one unit of reach is the budget divided by the target. The brand pays that
          price for every unit recognized, up to the target.
        </P>
        <DocTable
          caption="The symbols of the settlement formula"
          head={["Symbol", "Meaning"]}
          rows={[
            ["B", "Budget the brand locks, in the campaign token"],
            ["T", "Target, in organic impressions on X or organic views on YouTube"],
            ["M", "Creator threshold, 1% of T rounded up"],
            ["S", "Recognized reach of all creators who reached M"],
            ["G", "What the brand pays"],
          ]}
        />
        <Formula label="The settlement formula">
          {"G      = B × min(S, T) / T\nfee    = G / 5\npool   = 4 × G / 5\nrefund = B − G"}
        </Formula>
        <Points
          items={[
            {
              lead: "One unit of reach is worth B ÷ T.",
              text: "Reach above the target is not paid for, so G never exceeds B.",
            },
            {
              lead: "Fee, pool and refund always add up to B.",
              text: "Amounts are whole token units. Rounding leaves at most a few base units, and they stay with the brand.",
            },
            {
              lead: "Nothing recognized means nothing paid.",
              text: "With S = 0 the brand gets B back. The same holds if the campaign is not settled within ten days of its end.",
            },
          ]}
        />

        <DocSubheading>Example</DocSubheading>
        <P>
          The campaign has a budget of 100,000 USDC and a target of 1,000,000 views, so the
          threshold is 10,000 views. All amounts are in USDC.
        </P>
        <DocTable
          caption="A 100,000 USDC campaign at three levels of recognized views"
          head={["Recognized views", "Brand pays", "Creators", "Morcast", "Back to the brand"]}
          rows={[
            ["0", "0", "0", "0", "100,000"],
            ["640,000", "64,000", "51,200", "12,800", "36,000"],
            ["1,500,000", "100,000", "80,000", "20,000", "0"],
          ]}
          numeric
        />
      </DocSection>

      <DocSection id="payout" title="A creator's payout">
        <P>
          A creator's reach Q is the sum over their passing items, from all their accounts in the
          campaign. Their score s equals Q when Q is at least M, and 0 otherwise. Each creator
          receives the pool in proportion to their score.
        </P>
        <Formula label="A creator's payout">{"P = pool × s / S"}</Formula>
        <Points
          items={[
            {
              lead: "Below the target, a payout stands alone.",
              text: "It equals 0.8 × B × s ÷ T, so it does not depend on what other creators do. Above the target, creators share a fixed pool.",
            },
            {
              lead: "A threshold keeps out noise.",
              text: "A creator counts once their reach is at least 1% of the target.",
            },
            {
              lead: "Payouts add up exactly.",
              text: "Base units left by rounding go one each to the creators with the largest remainders.",
            },
          ]}
        />
        <P>
          In the example above with 640,000 recognized views, a creator with 300,000 views receives
          24,000 USDC. A creator with 9,000 views is below the threshold and receives nothing.
        </P>
      </DocSection>

      <DocSection id="tokens" title="Tokens">
        <P>
          A brand picks USDC or MOR for its campaign, and creators are paid in that token. Nobody
          needs MOR to use Morcast today. More MOR utility arrives in steps as the network grows,
          and the{" "}
          <Link to="/docs/roadmap" className={styles.link}>
            roadmap
          </Link>{" "}
          sets them out.
        </P>
      </DocSection>
    </DocPage>
  );
}
